<script context="module">
  function determineIfShown(pagesToShow, pagesToHide, path) {
    const showEverywhere = pagesToShow && pagesToShow.length === 0;
    const explicitlyShown = pagesToShow.includes(path);
    const explicitlyHidden = pagesToHide.includes(path);

    return (showEverywhere || explicitlyShown) && !explicitlyHidden;
  }

  function validateBannerDuplicates(banners) {
    if (!banners.length) {
      return {};
    }

    const conflicts = {};
    const globalBanners = {};

    // Collect all URLs mentioned in banners
    const allUrls = new Set();
    for (const banner of banners) {
      const { pagesToShow = [], pagesToHide = [] } = banner.fields;

      // track banners that are set to show everywhere
      if (pagesToShow.length === 0 && pagesToHide.length === 0) {
        globalBanners[banner.sys.id] = banner;
      }

      pagesToShow.forEach((url) => allUrls.add(url));
      pagesToHide.forEach((url) => allUrls.add(url));
    }

    const urlList = Array.from(allUrls);

    // for each possible URL, check which banners are visible there
    for (const path of urlList) {
      const visible = [];

      for (const banner of banners) {
        const { pagesToShow = [], pagesToHide = [] } = banner.fields;
        const shouldShow = determineIfShown(pagesToShow, pagesToHide, path);

        if (shouldShow) {
          visible.push(banner.sys.id);
        }
      }

      // if more than one banner is visible on the same page, log it as a conflict
      if (visible.length > 1) {
        conflicts[path] = visible;
      }
    }

    // check if there are multiple global banners
    if (Object.keys(globalBanners).length > 1) {
      conflicts[""] = Object.keys(globalBanners);
    }

    return conflicts;
  }

  /**
   * Determine which banners to show based on current path and an inclusion/exclusion list within each banner
   *
   * @param {string} currentPath
   * @param {Array} banners - Array of banner objects with `fields` property.
   * @returns {Array} matching banners
   */
  export function calculateVisibility(currentPath, banners = []) {
    if (!banners || banners.length === 0) {
      return [];
    }

    const matchingBanners = banners.reduce((acc, banner) => {
      const pagesToShow = banner.fields.pagesToShow || [];
      const pagesToHide = banner.fields.pagesToHide || [];
      const shouldShow = determineIfShown(pagesToShow, pagesToHide, currentPath);

      if (shouldShow) {
        return [...acc, banner];
      }

      return acc;
    }, []);

    return matchingBanners;
  }
</script>

<script>
  // @ts-check
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";
  import { Banner } from "@ObamaFoundation/of-design-system";
  import { BREAK_POINTS } from "$lib/utilities/constants";
  import { textToId } from "$lib/utilities/dom-helpers";
  import { isExternalUrl } from "$lib/utilities/url-helpers";
  import { mobileNavExpansionStore } from "$lib/stores";
  import isPreviewBuild from "$lib/utilities/isPreviewBuild";

  const isPreview = isPreviewBuild();

  export let data;

  const linkVariantMap = {
    textLink: "textLink",
    outlinedLink: "outline",
    filledLink: "filled"
  };

  const banners = data || [];
  const currentPath = $page?.url?.pathname || "/";

  let bannerDuplicates;
  let matchingBanners = [];
  let matchingBanner;
  let duplicateBannerError = "";

  if (isPreview) {
    bannerDuplicates = validateBannerDuplicates(banners);

    if (bannerDuplicates[""]) {
      duplicateBannerError = `There are currently ${bannerDuplicates[""].length} global banner(s) configured to display!`;
    } else {
      const duplicatePageCount = Object.keys(bannerDuplicates).length;

      if (duplicatePageCount && !bannerDuplicates[currentPath]) {
        duplicateBannerError = `${duplicatePageCount} page(s) have a banner conflict.`;

        for (const path in bannerDuplicates) {
          if (Object.hasOwn(bannerDuplicates, path)) {
            const banners = bannerDuplicates[path];
            duplicateBannerError += ` ${path} has these duplicate banners configured: ${banners.join(
              ", "
            )}.`;
          }
        }
      }
    }
  }

  matchingBanners = calculateVisibility(currentPath, banners);
  matchingBanner = matchingBanners.length > 0 ? matchingBanners[0] : null;

  const {
    headline: title,
    copy: body,
    cta: ctaData,
    theme: themeName
  } = matchingBanner?.fields || {};

  const cta = ctaData
    ? {
        text: ctaData.fields?.text,
        type: linkVariantMap[ctaData.fields?.type],
        url: ctaData.fields?.destination?.fields?.url,
        isExternal: isExternalUrl(ctaData.fields?.destination?.fields?.url),
        id: `banner-cta-${textToId(title ?? ctaData.sys.id)}`
      }
    : null;

  let isHidden = false;
  let screenWidth = 0;

  $: if (screenWidth && screenWidth < BREAK_POINTS.lg) {
    isHidden = $mobileNavExpansionStore;
  }
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#if isPreview && duplicateBannerError.length > 0}
  <Banner
    title="Too many banners configured to be displayed on some pages!"
    body={duplicateBannerError}
    themeName="red"
    isHidden={false}
  />
{/if}

{#if duplicateBannerError.length === 0 && isPreview && matchingBanners.length > 1}
  <Banner
    title="Too many banners configured for this page!"
    body={`There are currently ${matchingBanners.length} banners configured to display on this page.`}
    themeName="red"
    isHidden={false}
  />
{/if}

{#if matchingBanner}
  <Banner {title} {body} {cta} {themeName} {isHidden} />
{/if}

<slot />
